import { motion, useReducedMotion } from 'motion/react';
import { Mail } from 'lucide-react';
import { useAuth } from '@/features/auth/context/AuthContext';
import { AuthFooterBar, AuthTopBar, EditorialPanel } from '@/features/auth/components';

const ADMIN_EMAIL = 'admin@thegoldsmith.ai';

/**
 * Shown when a super admin has suspended the account (`accountStatus === 'banned'`).
 */
export default function AccountBlockedPage() {
  const reduceMotion = useReducedMotion();
  const { logout, user } = useAuth();

  return (
    <div className="min-h-screen flex bg-canvas text-ink-900">
      <EditorialPanel />

      <section className="flex-1 flex flex-col bg-surface relative">
        <AuthTopBar />

        <div className="flex-1 flex items-center justify-center px-5 sm:px-10 lg:px-14 safe-pt safe-pb">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-[400px] py-8 sm:py-10"
          >
            <header className="mb-9">
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-500 mb-3">
                Access restricted
              </p>
              <h1 className="font-headline text-[34px] leading-[1.1] text-ink-900">
                Your access has been restricted.
              </h1>
              <p className="mt-2.5 text-[14.5px] text-ink-500 leading-relaxed">
                An administrator has suspended this account. If you believe this is a mistake, contact us and we
                will help you resolve it.
              </p>
            </header>

            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700 leading-relaxed mb-5">
              You cannot use the application until access is restored. Use the button below to reach the team.
            </div>

            <div className="space-y-3.5">
              <motion.a
                href={`mailto:${ADMIN_EMAIL}?subject=Account%20access%20question`}
                whileHover={reduceMotion ? undefined : { y: -1 }}
                whileTap={{ scale: 0.99 }}
                className="btn-press btn-shine w-full h-12 rounded-xl bg-ink-900 hover:bg-ink-800 text-white font-semibold text-[14px] tracking-wide flex items-center justify-center gap-2 cursor-pointer focus-ring"
              >
                <Mail size={16} strokeWidth={2.2} />
                <span className="tabular">{ADMIN_EMAIL}</span>
              </motion.a>

              <p className="text-center text-[13px] text-ink-500 pt-2">
                <button
                  type="button"
                  onClick={() => logout()}
                  className="font-semibold text-ink-800 hover:text-gold-600 transition-colors cursor-pointer"
                >
                  Sign out
                  {user?.email ? ` (${user.email})` : ''}
                </button>
              </p>
            </div>
          </motion.div>
        </div>

        <AuthFooterBar />
      </section>
    </div>
  );
}
